/**
 * Redemption Service
 * Handles the complete offer redemption flow
 */
var __awaiter = (this && this.__awaiter) || function (thisArg, _arguments, P, generator) {
    function adopt(value) { return value instanceof P ? value : new P(function (resolve) { resolve(value); }); }
    return new (P || (P = Promise))(function (resolve, reject) {
        function fulfilled(value) { try { step(generator.next(value)); } catch (e) { reject(e); } }
        function rejected(value) { try { step(generator["throw"](value)); } catch (e) { reject(e); } }
        function step(result) { result.done ? resolve(result.value) : adopt(result.value).then(fulfilled, rejected); }
        step((generator = generator.apply(thisArg, _arguments || [])).next());
    });
};
import { MagicClient } from "../data/auth/magic-client";
import { OrderVerifier, SupportedNetwork, } from "../types";
import { ethers } from "ethers";
import { same_brand_reward_redeption_magic, spend_reward_magic, } from "@developeruche/runtime-sdk";
import { relay, usersServiceWithPermit } from "@developeruche/protocol-core";
export class RedemptionService {
    constructor(authAPI, rewardAPI, redemptionAPI, magicConfig, openRewardDiamond, chainId, rpcUrl, runtimeUrl, meApiKey, apiV1Url, gelatoApiKey, brandId) {
        this.magicClient = null;
        this.walletAddress = null;
        this.balances = [];
        this.meProtocolLoggedIn = false;
        this.currentEmail = null;
        this.meProtocolToken = null;
        this.currentOrder = null;
        this.authAPI = authAPI;
        this.rewardAPI = rewardAPI;
        this.redemptionAPI = redemptionAPI;
        this.magicClient = new MagicClient(magicConfig);
        this.openRewardDiamond = openRewardDiamond;
        this.chainId = chainId;
        this.rpcUrl = rpcUrl;
        this.runtimeUrl = runtimeUrl;
        this.meApiKey = meApiKey;
        this.apiV1Url = apiV1Url;
        this.gelatoApiKey = gelatoApiKey;
        this.brandId = brandId;
    }
    /**
     * Get email from stored or config
     */
    getEmail() {
        return this.currentEmail || this.authAPI.getUserEmail() || null;
    }
    /**
     * Set email (for OTP flow)
     */
    setEmail(email) {
        this.currentEmail = email;
    }
    /**
     * Check if Magic is configured
     */
    isMagicConfigured() {
        return this.magicClient !== null;
    }
    /**
     * Check if user is authenticated
     */
    isAuthenticated() {
        return __awaiter(this, void 0, void 0, function* () {
            if (!this.magicClient) {
                return false;
            }
            try {
                return yield this.magicClient.isLoggedIn();
            }
            catch (error) {
                console.error("Error checking authentication:", error);
                return false;
            }
        });
    }
    /**
     * Get wallet address (with force refresh option)
     */
    /**
     * Ensure user is logged in to Magic (triggers OTP if not)
     * Also verifies that the logged-in email matches the SDK email
     */
    ensureMagicLogin(email) {
        return __awaiter(this, void 0, void 0, function* () {
            var _a;
            if (!this.magicClient) {
                throw new Error("Magic is not configured");
            }
            try {
                // Check if already logged in
                const isLoggedIn = yield this.magicClient.isLoggedIn();
                if (isLoggedIn) {
                    // Verify the logged-in email matches the SDK email
                    try {
                        const metadata = yield this.magicClient.getUserMetadata();
                        const loggedInEmail = (_a = metadata.email) === null || _a === void 0 ? void 0 : _a.toLowerCase().trim();
                        const sdkEmail = email.toLowerCase().trim();
                        if (loggedInEmail !== sdkEmail) {
                            console.log(`🔧 Email mismatch: Logged in as "${loggedInEmail}", but SDK expects "${sdkEmail}". Logging out...`);
                            // Logout and re-login with the correct email
                            yield this.magicClient.logout();
                            yield this.magicClient.loginWithEmailOTP(email);
                        }
                        else {
                            console.log(`✅ Already logged in with correct email: ${loggedInEmail}`);
                        }
                    }
                    catch (error) {
                        console.error("Error verifying logged-in email:", error);
                        // If we can't verify, logout and re-login to be safe
                        yield this.magicClient.logout();
                        yield this.magicClient.loginWithEmailOTP(email);
                    }
                }
                else {
                    // Not logged in, trigger Magic OTP login
                    console.log(`🔐 Not logged in. Triggering Magic OTP for: ${email}`);
                    yield this.magicClient.loginWithEmailOTP(email);
                }
            }
            catch (error) {
                console.error("Error logging in to Magic:", error);
                throw new Error("Failed to login with Magic Link. Please try again.");
            }
        });
    }
    getWalletAddress() {
        return __awaiter(this, arguments, void 0, function* (forceRefresh = false) {
            // Use cached value unless force refresh is requested
            if (this.walletAddress && !forceRefresh) {
                return this.walletAddress;
            }
            if (!this.magicClient) {
                throw new Error("Magic is not configured");
            }
            try {
                // First verify user is logged in
                const isLoggedIn = yield this.magicClient.isLoggedIn();
                if (!isLoggedIn) {
                    throw new Error("User is not logged in to Magic");
                }
                // Retry logic for fetching wallet address (Magic might need a moment)
                let retries = 3;
                let lastError = null;
                while (retries > 0) {
                    try {
                        // Fetch wallet address from Magic
                        this.walletAddress = yield this.magicClient.getWalletAddress();
                        if (this.walletAddress) {
                            return this.walletAddress;
                        }
                        // If null, wait and retry
                        console.warn("Wallet address is null, retrying...");
                        yield new Promise((resolve) => setTimeout(resolve, 1000));
                        retries--;
                    }
                    catch (err) {
                        lastError = err;
                        console.warn(`Error on attempt ${4 - retries}:`, err);
                        yield new Promise((resolve) => setTimeout(resolve, 1000));
                        retries--;
                    }
                }
                // All retries failed
                throw new Error(`Failed to retrieve wallet address from Magic after 3 attempts. Last error: ${(lastError === null || lastError === void 0 ? void 0 : lastError.message) || "Unknown"}`);
            }
            catch (error) {
                console.error("Error getting wallet address:", error);
                throw error;
            }
        });
    }
    /**
     * Clear cached wallet address (useful after logout or re-authentication)
     */
    clearWalletAddressCache() {
        this.walletAddress = null;
    }
    /**
     * Send OTP to email
     */
    sendOTP(email) {
        return __awaiter(this, void 0, void 0, function* () {
            if (!this.magicClient) {
                throw new Error("Magic is not configured");
            }
            try {
                this.currentEmail = email; // Store email for later use
                yield this.magicClient.loginWithEmailOTP(email);
            }
            catch (error) {
                console.error("Error sending OTP:", error);
                throw error;
            }
        });
    }
    /**
     * Login to ME Protocol (creates account if new user)
     */
    loginToMEProtocol() {
        return __awaiter(this, void 0, void 0, function* () {
            try {
                const email = this.getEmail();
                if (!email) {
                    throw new Error("Email not available");
                }
                // Force refresh wallet address to ensure we have the latest from Magic
                const walletAddress = yield this.getWalletAddress(true);
                // Login to ME Protocol (this creates account if new user)
                const loginResponse = yield this.authAPI.meProtocolLogin(email, walletAddress);
                if (loginResponse.data.user && loginResponse.data.token) {
                    this.meProtocolLoggedIn = true;
                    this.meProtocolToken = loginResponse.data.token; // Store the token
                }
                else {
                    throw new Error("Login failed");
                }
            }
            catch (error) {
                console.error("Error logging in to ME Protocol:", error);
                throw error;
            }
        });
    }
    /**
     * Check if user is logged in to ME Protocol
     */
    isMEProtocolLoggedIn() {
        return this.meProtocolLoggedIn;
    }
    /**
     * Fetch user's reward balances
     */
    fetchBalances() {
        return __awaiter(this, void 0, void 0, function* () {
            try {
                const walletAddress = yield this.getWalletAddress();
                if (!this.meProtocolToken) {
                    throw new Error("ME Protocol token not available. Please login first.");
                }
                const balances = yield this.rewardAPI.fetchRewardBalances(walletAddress, this.meProtocolToken);
                this.balances = balances;
                return balances;
            }
            catch (error) {
                console.error("Error fetching balances:", error);
                throw error;
            }
        });
    }
    /**
     * Calculate swap amount for redemption
     */
    calculateSwapAmount(selectedRewardAddress, offerDetail, selectedVariantId) {
        return __awaiter(this, void 0, void 0, function* () {
            var _a;
            try {
                const walletAddress = yield this.getWalletAddress();
                if (!this.meProtocolToken) {
                    throw new Error("ME Protocol token not available. Please login first.");
                }
                // Use provided variant ID, or first variant if available
                let variantId = selectedVariantId;
                if (!variantId &&
                    offerDetail.offerVariants &&
                    offerDetail.offerVariants.length > 0) {
                    // Use the underlying product variant id, not the offerVariant id
                    variantId =
                        ((_a = offerDetail.offerVariants[0].variant) === null || _a === void 0 ? void 0 : _a.id) ||
                            offerDetail.offerVariants[0].variantId ||
                            undefined;
                }
                const payload = {
                    walletAddress,
                    inputRewardAddress: selectedRewardAddress, // The reward the user wants to use
                    outPutRewardAddress: offerDetail.reward.contractAddress, // The reward from the offer
                    redemptionMethodId: offerDetail.redemptionMethod.id,
                    offerId: offerDetail.id,
                    variantId,
                    brandId: offerDetail.brand.id,
                };
                const result = yield this.rewardAPI.fetchSwapAmount(payload, this.meProtocolToken);
                return result;
            }
            catch (error) {
                console.error("Error calculating swap amount:", error);
                throw error;
            }
        });
    }
    /**
     * Update Magic network configuration
     */
    updateNetwork(brandNetwork) {
        return __awaiter(this, void 0, void 0, function* () {
            if (!this.magicClient) {
                return;
            }
            try {
                // Network configuration is already handled by env config
                // Brand network is used for Magic Link initialization
            }
            catch (error) {
                console.error("Error updating network:", error);
            }
        });
    }
    /**
     * Check if user can afford the offer with selected reward
     */
    canAffordOffer(selectedReward, amountNeeded) {
        return selectedReward.balance >= amountNeeded;
    }
    /**
     * Get cached balances
     */
    getCachedBalances() {
        return this.balances;
    }
    /**
     * Clear wallet address cache
     */
    clearCache() {
        this.walletAddress = null;
        this.balances = [];
    }
    /**
     * Execute same brand redemption transaction
     * Used when the selected reward is from the same brand as the offer
     */
    executeSameBrandRedemption(rewardAddress, rewardId, amount, offerId, redemptionMethodId, brandId, variantId) {
        return __awaiter(this, void 0, void 0, function* () {
            let pushTransactionRef = null;
            try {
                if (!this.magicClient) {
                    throw new Error("Magic is not configured");
                }
                if (!this.meProtocolToken) {
                    throw new Error("ME Protocol token not available. Please login first.");
                }
                // Validate amount
                if (!amount || amount === "undefined" || isNaN(Number(amount))) {
                    throw new Error("Invalid amount value for transaction");
                }
                console.log("Same-brand transaction params:", { amount });
                // Get Magic provider and signer
                const provider = yield this.magicClient.getProvider();
                const web3Provider = new ethers.providers.Web3Provider(provider);
                const accounts = yield web3Provider.listAccounts();
                const signer = web3Provider.getSigner(accounts[0]);
                console.log("SIGNER", yield signer.getAddress());
                // Prepare transaction
                const rewardAmount = ethers.utils.parseEther(amount);
                // Validate all parameters
                if (!rewardAddress || rewardAddress === "undefined") {
                    throw new Error(`Invalid reward address: ${rewardAddress}`);
                }
                if (!this.runtimeUrl) {
                    throw new Error("Runtime URL is not configured");
                }
                console.log("same_brand_reward_redeption_magic params:", {
                    rewardAddress,
                    rewardAmount: rewardAmount.toString(),
                    rewardAmountHex: rewardAmount.toHexString(),
                    chainId: this.chainId,
                    runtimeUrl: this.runtimeUrl,
                    signerAddress: signer,
                });
                // Call runtime SDK
                const result = yield same_brand_reward_redeption_magic(rewardAddress, rewardAmount, ethers.BigNumber.from(this.chainId), signer, this.runtimeUrl);
                console.log("RESULT", result);
                const hash = result.hash;
                console.log("FROM", result.from);
                // Push transaction to runtime
                pushTransactionRef = yield this.redemptionAPI.pushTransaction({
                    params: {
                        from: result.from,
                        data: result.data,
                        nonce: result.nonce,
                        r: result.r,
                        s: result.s,
                        v: result.v,
                        hash: result.hash,
                    },
                }, this.meProtocolToken);
                if (!pushTransactionRef) {
                    throw new Error("No data returned from push transaction");
                }
                // Process order
                const order = yield this.processOrder(pushTransactionRef.result, hash, rewardId, amount, offerId, redemptionMethodId, OrderVerifier.RUNTIME, variantId);
                return order;
            }
            catch (error) {
                // Refund if transaction was pushed but order processing failed
                if ((pushTransactionRef === null || pushTransactionRef === void 0 ? void 0 : pushTransactionRef.result) && this.meProtocolToken) {
                    try {
                        yield this.redemptionAPI.refundTask({ spend_data: pushTransactionRef.result }, this.meProtocolToken);
                    }
                    catch (refundError) {
                        console.error("Error refunding task:", refundError);
                    }
                }
                throw error;
            }
        });
    }
    /**
     * Execute cross brand redemption transaction
     * Used when the selected reward is from a different brand than the offer
     */
    executeCrossBrandRedemption(rewardAddress, rewardId, amount, neededAmount, brandRewardAddress, offerId, redemptionMethodId, brandId, brandNetwork, variantId) {
        return __awaiter(this, void 0, void 0, function* () {
            let pushTransactionRef = null;
            try {
                if (!this.magicClient) {
                    throw new Error("Magic is not configured");
                }
                if (!this.meProtocolToken) {
                    throw new Error("ME Protocol token not available. Please login first.");
                }
                if (!this.walletAddress) {
                    throw new Error("Wallet address not available");
                }
                // Validate amounts
                if (!neededAmount ||
                    neededAmount === "undefined" ||
                    isNaN(Number(neededAmount))) {
                    throw new Error("Invalid needed amount value for transaction");
                }
                if (!amount || amount === "undefined" || isNaN(Number(amount))) {
                    throw new Error("Invalid amount value for transaction");
                }
                console.log("Cross-brand transaction params:", { neededAmount, amount });
                // Get Magic provider and signer
                const provider = yield this.magicClient.getProvider();
                const web3Provider = new ethers.providers.Web3Provider(provider);
                const accounts = yield web3Provider.listAccounts();
                const signer = web3Provider.getSigner(accounts[0]);
                // Prepare spend info
                const amountOfRewardAtHand = ethers.utils.parseEther(neededAmount);
                const expectedAmountOfTargetedReward = ethers.utils.parseEther(amount);
                const spendInfo = {
                    rewardAtHand: rewardAddress,
                    targettedReward: brandRewardAddress,
                    amountOfRewardAtHand,
                    expectedAmountOfTargetedReward,
                };
                console.log("SPEND REWARD MAGIC", rewardAddress, amountOfRewardAtHand, this.openRewardDiamond, signer, this.runtimeUrl, spendInfo);
                // Call runtime SDK
                const result = yield spend_reward_magic(rewardAddress, amountOfRewardAtHand, this.openRewardDiamond, ethers.BigNumber.from(this.chainId), signer, this.runtimeUrl);
                // Push transaction to runtime
                pushTransactionRef = yield this.redemptionAPI.pushTransaction({
                    params: {
                        from: result.from,
                        data: result.data,
                        nonce: result.nonce,
                        r: result.r,
                        s: result.s,
                        v: result.v,
                        hash: result.hash,
                    },
                }, this.meProtocolToken);
                if (!pushTransactionRef) {
                    throw new Error("No data returned from push transaction");
                }
                // Get vault permit
                const vaultParams = {
                    owner: pushTransactionRef.owner,
                    count: pushTransactionRef.count,
                    globalHash: pushTransactionRef.globalHash,
                    prefixedHash: pushTransactionRef.prefixedHash,
                    r: pushTransactionRef.sig.r,
                    s: pushTransactionRef.sig.s,
                    v: pushTransactionRef.sig.v,
                    reward: pushTransactionRef.reward,
                    spender: pushTransactionRef.spender,
                    value: pushTransactionRef.value,
                };
                console.log("DATUM", spendInfo, vaultParams, this.openRewardDiamond, this.rpcUrl);
                const datum = yield usersServiceWithPermit.spendRewardsOnAnotherBrandWithVaultPermit(spendInfo, vaultParams, this.openRewardDiamond, this.rpcUrl);
                if (!(datum === null || datum === void 0 ? void 0 : datum.data)) {
                    throw new Error("No data returned from vault permit");
                }
                console.log("RELAY", {
                    from: this.walletAddress,
                    data: datum.data,
                    to: this.openRewardDiamond,
                }, signer, this.meApiKey, this.apiV1Url, this.gelatoApiKey, this.rpcUrl, this.chainId, this.openRewardDiamond, brandId, pushTransactionRef);
                // Relay via Gelato
                const { taskId } = yield relay({
                    from: this.walletAddress,
                    data: datum.data,
                    to: this.openRewardDiamond,
                }, signer, this.meApiKey, this.apiV1Url, this.gelatoApiKey, this.rpcUrl, this.chainId, this.openRewardDiamond, brandId, false, "", brandNetwork === SupportedNetwork.HEDERA);
                // Process order
                const order = yield this.processOrder(pushTransactionRef, taskId, rewardId, amount, offerId, redemptionMethodId, OrderVerifier.GELATO, variantId);
                return order;
            }
            catch (error) {
                // Refund if transaction was pushed but order processing failed
                if ((pushTransactionRef === null || pushTransactionRef === void 0 ? void 0 : pushTransactionRef.result) && this.meProtocolToken) {
                    try {
                        yield this.redemptionAPI.refundTask({ spend_data: pushTransactionRef.result }, this.meProtocolToken);
                    }
                    catch (refundError) {
                        console.error("Error refunding task:", refundError);
                    }
                }
                throw error;
            }
        });
    }
    /**
     * Process order after successful transaction
     */
    processOrder(spendData, taskId, rewardId, amount, offerId, redemptionMethodId, verifier, variantId) {
        return __awaiter(this, void 0, void 0, function* () {
            if (!this.meProtocolToken) {
                throw new Error("ME Protocol token not available");
            }
            const payload = {
                task_id: taskId,
                reward_id: rewardId,
                target_reward_id: rewardId, // Same for now, adjust if needed
                verifier,
                spend_data: spendData,
                offer_id: offerId,
                amount,
                redemption_method_id: redemptionMethodId,
                offer_variants: variantId ? [variantId] : [],
            };
            const response = yield this.redemptionAPI.processOrder(payload, this.meProtocolToken);
            this.currentOrder = response.order;
            return response.order;
        });
    }
    /**
     * Get checkout URL for the redeemed offer
     */
    getCheckoutUrl(brandId, productVariantIdOnBrandSite) {
        return __awaiter(this, void 0, void 0, function* () {
            if (!this.currentOrder) {
                throw new Error("No order available. Please redeem an offer first.");
            }
            if (!this.meProtocolToken) {
                throw new Error("ME Protocol token not available");
            }
            const payload = {
                brandId,
                discountCode: this.currentOrder.coupon.code,
                productVariantIdOnBrandSite,
            };
            return yield this.redemptionAPI.getCheckoutUrl(payload, this.meProtocolToken);
        });
    }
    /**
     * Get current order
     */
    getCurrentOrder() {
        return this.currentOrder;
    }
    /**
     * Clear current order
     */
    clearCurrentOrder() {
        this.currentOrder = null;
    }
    /**
     * Logout - REMOVED: Users should stay logged in
     * Kept for backward compatibility but does nothing
     */
    logout() {
        return __awaiter(this, void 0, void 0, function* () {
            // Do nothing - users stay logged in with Magic
        });
    }
}
